import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../component-styles/ReportsShared.css';
import API_BASE from '../../utils/api.js';
import { exportRowsToExcel } from '../../utils/excelExport.js';


function formatCurrency(v) {
  if (v == null || Number.isNaN(Number(v))) return '$0.00';
  return Number(v).toLocaleString(undefined, { style: 'currency', currency: 'USD' });
}

function formatDate(v) {
  if (!v) return '-';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '-';
  return d.toLocaleDateString();
}

// Stripe payout dates are calendar days (YYYY-MM-DD); parsing them with
// new Date() would read them as UTC midnight and show the previous day in US
// timezones.
function formatDay(ymd) {
  const [y, m, d] = String(ymd || '')
    .split('-')
    .map(Number);
  if (!y || !m || !d) return '-';
  return new Date(y, m - 1, d).toLocaleDateString();
}

// The payment record already knows the client-paid processing fee, so it
// still shows if Stripe's details can't be loaded.
function getProcessingFee(row, stripeDetails) {
  return stripeDetails ? stripeDetails.processingFee : row.processingFee;
}

function getDefaultDateRange() {
  const year = new Date().getFullYear();
  return { start: `${year}-01-01`, end: `${year}-12-31` };
}

function normalizeProject(raw) {
  return {
    id: String(raw.projectId || raw.id || raw.name || ''),
    name: raw.projectNickname || raw.name || raw.title || 'Untitled Project',
  };
}

export default function ClientPaymentReportPage() {
  const navigate = useNavigate();
  const userId = localStorage.getItem('userId');
  const defaultRange = useMemo(() => getDefaultDateRange(), []);

  const [projects, setProjects] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  const [startDate, setStartDate] = useState(defaultRange.start);
  const [endDate, setEndDate] = useState(defaultRange.end);
  const [projectFilter, setProjectFilter] = useState('all');
  const [stripeDetails, setStripeDetails] = useState({});
  const [stripeStatus, setStripeStatus] = useState('loading');

  useEffect(() => {
    async function loadProjects() {
      if (!userId) {
        setProjects([]);
        return;
      }

      try {
        const res = await fetch(`${API_BASE}/api/projects/${encodeURIComponent(userId)}`);
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        const arr = Array.isArray(data) ? data : data?.Items || data?.items || [];
        setProjects(arr.map(normalizeProject));
      } catch (err) {
        console.error('Failed to load projects', err);
        setProjects([]);
      }
    }

    loadProjects();
  }, [userId]);

  useEffect(() => {
    async function loadPayments() {
      if (!userId) {
        setRows([]);
        setLoading(false);
        return;
      }

      setLoading(true);

      const projectList = projects.length ? projects : [];
      if (!projectList.length) {
        setRows([]);
        setLoading(false);
        return;
      }

      const paymentRows = [];

  function getDocNumber(doc, type) {
    if (type === 'Invoice') return doc.invoiceNumber || doc.docNumber || '-';
    if (type === 'Proposal') return doc.proposalNumber || doc.docNumber || '-';
    if (type === 'Purchase Order') return doc.orderNumber || doc.poNumber || doc.poId || '-';
    if (type === 'Retainer') return doc.retainerNumber || doc.docNumber || '-';
    if (type === 'Time Billing') return doc.invoiceNumber || doc.docNumber || '-';
    return doc.docNumber || '-';
  }

  const addPayments = (items, meta) => {
    (items || []).forEach((doc) => {
      const history = Array.isArray(doc.paymentHistory) ? doc.paymentHistory : [];
      history.forEach((p) => {
        // Converting proposal items to an invoice moves what was paid on the
        // proposal onto that invoice — recorded as an outflow on the proposal
        // and a deposit on the invoice. It's still the one client payment,
        // already listed on the proposal where it was made.
        if (p.paymentType === 'invoice_transfer' || p.sourceProposalId) return;
        // Purchase orders converted before this was fixed got copies of the
        // client's proposal and invoice payments. They're listed where they
        // were made.
        if (p.convertedCarryover) return;
        const docNumber = getDocNumber(doc, meta.type);
        const baseId =
          doc.id ||
          doc.invoiceId ||
          doc.proposalId ||
          doc.poId ||
          doc.retainerId ||
          doc.tbInvoiceId ||
          doc.docId ||
          '';
        paymentRows.push({
          paymentId: p.paymentId || `${meta.type}-${baseId || Date.now()}`,
          projectId: String(meta.projectId || ''),
          projectName: meta.projectName || 'Untitled Project',
          clientName: doc.client?.name || doc.clientName || meta.clientName || meta.projectName || '-',
          docType: meta.type,
          docNumber,
          date: p.date || '',
          method: p.method || p.paymentType || 'manual',
          reference: p.reference || '-',
          amount: Number(p.amount || 0),
          paymentType: p.paymentType || 'manual',
          // Refund entries carry the original intent id too — only the
          // payment itself should pick up that intent's Stripe fee/payout.
          stripeIntentId: p.paymentType === 'stripe' ? p.stripeIntentId || '' : '',
          processingFee: Number(p.processingFee || 0),
          invoiceId: doc.invoiceId || '',
          proposalId: doc.proposalId || '',
          poId: doc.poId || '',
          tbInvoiceId: doc.tbInvoiceId || '',
        });
      });
    });
  };

      try {
        const results = await Promise.all(
          projectList.map(async (project) => {
            const projectId = project.id;
            const projectName = project.name;

            const requests = [
              fetch(
                `${API_BASE}/api/invoices/${encodeURIComponent(userId)}/${encodeURIComponent(
                  projectId
                )}`
              )
                .then((r) => (r.ok ? r.json() : []))
                .then((list) =>
                  addPayments(list, {
                    type: 'Invoice',
                    projectId,
                    projectName,
                    clientName: projectName,
                  })
                ),
              fetch(
                `${API_BASE}/api/proposals/${encodeURIComponent(userId)}/${encodeURIComponent(
                  projectId
                )}`
              )
                .then((r) => (r.ok ? r.json() : []))
                .then((list) =>
                  addPayments(list, {
                    type: 'Proposal',
                    projectId,
                    projectName,
                    clientName: projectName,
                  })
                ),
              fetch(
                `${API_BASE}/api/purchase-orders/${encodeURIComponent(userId)}/${encodeURIComponent(
                  projectId
                )}`
              )
                .then((r) => (r.ok ? r.json() : []))
                .then((list) =>
                  addPayments(list, {
                    type: 'Purchase Order',
                    projectId,
                    projectName,
                    clientName: projectName,
                  })
                ),
              fetch(`${API_BASE}/api/retainers/${encodeURIComponent(projectId)}`, {
                headers: { userid: userId },
              })
                .then((r) => (r.ok ? r.json() : []))
                .then((list) =>
                  addPayments(list, {
                    type: 'Retainer',
                    projectId,
                    projectName,
                    clientName: projectName,
                  })
                ),
              fetch(
                `${API_BASE}/api/timebilling-invoices/project/${encodeURIComponent(
                  userId
                )}/${encodeURIComponent(projectId)}`
              )
                .then((r) => (r.ok ? r.json() : []))
                .then((list) =>
                  addPayments(list, {
                    type: 'Time Billing',
                    projectId,
                    projectName,
                    clientName: projectName,
                  })
                ),
            ];

            await Promise.all(requests);
            return true;
          })
        );

        if (results) {
          // Proposals converted whole (before items could be invoiced one at a
          // time) had their payments copied onto the invoice under the same
          // paymentId. Each is still one payment, listed on the proposal where
          // it was made.
          const proposalPaymentIds = new Set(
            paymentRows.filter((r) => r.docType === 'Proposal').map((r) => r.paymentId)
          );
          setRows(
            paymentRows.filter(
              (r) => !(r.docType === 'Invoice' && proposalPaymentIds.has(r.paymentId))
            )
          );
        }
      } catch (err) {
        console.error('Failed to load payments', err);
        setRows([]);
      } finally {
        setLoading(false);
      }
    }

    loadPayments();
  }, [projects, userId]);

  // Fee, net deposit and payout per online payment, straight from the
  // studio's Stripe account — what they need to match bank deposits in QB.
  useEffect(() => {
    let cancelled = false;
    async function loadStripeDetails() {
      setStripeStatus('loading');
      try {
        const params = new URLSearchParams({ startDate, endDate });
        const res = await fetch(`${API_BASE}/api/reports/stripe-payment-details?${params}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('idToken')}` },
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        if (cancelled) return;
        setStripeDetails(data.payments || {});
        setStripeStatus('ready');
      } catch (err) {
        console.error('Failed to load Stripe payment details', err);
        if (cancelled) return;
        setStripeDetails({});
        setStripeStatus('error');
      }
    }

    loadStripeDetails();
    return () => {
      cancelled = true;
    };
  }, [startDate, endDate]);

  const filteredRows = useMemo(() => {
    const filtered = rows.filter((r) => {
      if (projectFilter !== 'all' && r.projectId !== projectFilter) return false;
      const date = String(r.date || '').slice(0, 10);
      if (startDate && date && date < startDate) return false;
      if (endDate && date && date > endDate) return false;
      return true;
    });
    filtered.sort((a, b) => (String(a.date || '') < String(b.date || '') ? 1 : -1));
    return filtered;
  }, [rows, projectFilter, startDate, endDate]);

  const hasStripeRows = useMemo(() => filteredRows.some((r) => r.stripeIntentId), [filteredRows]);

  const totals = useMemo(() => {
    return filteredRows.reduce(
      (acc, r) => {
        acc.total += Number(r.amount || 0);
        if (r.stripeIntentId) {
          const details = stripeDetails[r.stripeIntentId];
          acc.processingFees += Number(getProcessingFee(r, details)) || 0;
          acc.stripeFees += Number(details?.fee || 0);
        }
        return acc;
      },
      { total: 0, processingFees: 0, stripeFees: 0 }
    );
  }, [filteredRows, stripeDetails]);

  function exportExcel() {
    const headers = [
      'Date',
      'Project',
      'Client',
      'Type',
      'Document #',
      'Method',
      'Reference',
      'Amount',
      ...(hasStripeRows
        ? [
            'Processing Fee',
            'Stripe Fee',
            'Net Deposit',
            'Payout Date',
            'Payout Total',
            'Payout ID',
          ]
        : []),
    ];
    const rowsForExport = filteredRows.map((r) => {
      const details = stripeDetails[r.stripeIntentId];
      const stripeCells = !r.stripeIntentId
        ? ['', '', '', '', '', '']
        : [
            Number(getProcessingFee(r, details) || 0),
            details ? details.fee : '',
            details ? details.net : '',
            details?.payout?.arrivalDate || '',
            details?.payout ? details.payout.amount : '',
            details?.payout?.id || '',
          ];
      return [
        r.date ? String(r.date).slice(0, 10) : '',
        r.projectName,
        r.clientName,
        r.docType,
        r.docNumber,
        r.method,
        r.reference,
        Number(r.amount || 0),
        ...(hasStripeRows ? stripeCells : []),
      ];
    });

    exportRowsToExcel({
      filename: 'client-payment-report.xlsx',
      sheetName: 'Client Payments',
      headers,
      rows: rowsForExport,
    });
  }

  function getDocumentPath(row) {
    const projectId = String(row.projectId || '').trim();
    if (!projectId) return '';
    if (row.docType === 'Invoice' && row.invoiceId) {
      return `/projects/${projectId}/invoices/${row.invoiceId}`;
    }
    if (row.docType === 'Proposal' && row.proposalId) {
      return `/projects/${projectId}/proposals/${row.proposalId}`;
    }
    if (row.docType === 'Purchase Order' && row.poId) {
      return `/projects/${projectId}/purchase-orders/${row.poId}`;
    }
    if (row.docType === 'Time Billing' && row.tbInvoiceId) {
      return `/projects/${projectId}/timebilling-invoices/${row.tbInvoiceId}`;
    }
    return '';
  }

  return (
    <div className="report-shell">
      <div className="report-shell-inner">
        <div className="report-header">
          <div className="report-title">
            <h1>Client Payment Report</h1>
            <div className="muted">
              {startDate} - {endDate}
            </div>
          </div>

          <div className="report-actions">
            <button className="report-btn-secondary" onClick={() => navigate('/reports')}>
              Back
            </button>
            <button className="report-btn-primary" onClick={exportExcel}>
              Export Excel
            </button>
          </div>
        </div>

        <div className="report-filters">
          <select
            className="report-select"
            value={projectFilter}
            onChange={(e) => setProjectFilter(e.target.value)}
          >
            <option value="all">All Projects</option>
            {projects.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}
              </option>
            ))}
          </select>

          <input
            className="report-input"
            type="date"
            value={startDate}
            onChange={(e) => setStartDate(e.target.value)}
          />
          <input
            className="report-input"
            type="date"
            value={endDate}
            onChange={(e) => setEndDate(e.target.value)}
          />
        </div>

        <div className="report-card">
          <div className="report-metrics">
            <div className="report-metric">
              <div className="report-metric-value">{formatCurrency(totals.total)}</div>
              <div className="report-metric-label">Total Payments</div>
            </div>
            {hasStripeRows && (
              <>
                <div className="report-metric">
                  <div className="report-metric-value">{formatCurrency(totals.processingFees)}</div>
                  <div className="report-metric-label">Processing Fees Paid by Clients</div>
                </div>
                <div className="report-metric">
                  <div className="report-metric-value">{formatCurrency(totals.stripeFees)}</div>
                  <div className="report-metric-label">Stripe Fees</div>
                </div>
              </>
            )}
          </div>
          {hasStripeRows && stripeStatus === 'error' && (
            <div className="report-subtext">
              Stripe fee and payout details couldn't be loaded. Try refreshing the page.
            </div>
          )}
        </div>

        <div className="report-table-wrap">
          {loading ? (
            <div className="report-empty">Loading client payments...</div>
          ) : filteredRows.length === 0 ? (
            <div className="report-empty">No payments found.</div>
          ) : (
            <table className="report-table" role="table" aria-label="Client payments">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Project</th>
                  <th>Client</th>
                  <th>Type</th>
                  <th>Document #</th>
                  <th>Method</th>
                  <th>Reference</th>
                  <th className="right">Amount</th>
                  {hasStripeRows && (
                    <>
                      <th className="right">Processing Fee</th>
                      <th className="right">Stripe Fee</th>
                      <th className="right">Net Deposit</th>
                      <th>Payout</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {filteredRows.map((r) => {
                  const details = stripeDetails[r.stripeIntentId];
                  const missing = !r.stripeIntentId || stripeStatus !== 'loading' ? '-' : '…';
                  return (
                    <tr key={`${r.projectId}-${r.paymentId}`}>
                      <td>{formatDate(r.date)}</td>
                      <td>{r.projectName}</td>
                      <td>{r.clientName}</td>
                      <td>{r.docType}</td>
                      <td>
                        {getDocumentPath(r) ? (
                          <a href={getDocumentPath(r)}>{r.docNumber}</a>
                        ) : (
                          r.docNumber
                        )}
                      </td>
                      <td>{r.method}</td>
                      <td>{r.reference}</td>
                      <td className="right">{formatCurrency(r.amount)}</td>
                      {hasStripeRows && (
                        <>
                          <td className="right">
                            {r.stripeIntentId ? formatCurrency(getProcessingFee(r, details)) : '-'}
                          </td>
                          <td className="right">
                            {details ? formatCurrency(details.fee) : missing}
                          </td>
                          <td className="right">
                            {details ? formatCurrency(details.net) : missing}
                          </td>
                          <td>
                            {details?.payout ? (
                              <>
                                {formatDay(details.payout.arrivalDate)}
                                {details.payout.status !== 'paid' &&
                                  ` (${details.payout.status.replace('_', ' ')})`}
                                <span className="report-subtext">
                                  {formatCurrency(details.payout.amount)} deposit
                                </span>
                              </>
                            ) : details ? (
                              'Pending'
                            ) : (
                              missing
                            )}
                          </td>
                        </>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
